import { z } from "zod";
import {
  careOfDeliverySchema,
  consigneeAndDeliverySchema,
  shipperSchema,
} from "../../services/prePlanningService.types";
import { tagSchema } from "../freight/tag.type";

export type ReasonCodes = {
  id: number;
  name: string;
};

export const osadAssignmentSchema = z.object({
  reasonId: z.number(),
  moduleId: z.number().nullish(),
  shipmentIds: z.array(z.number()),
});

export type OsadAssignment = z.infer<typeof osadAssignmentSchema>;

const osadShipmentTagSchema = tagSchema
  .extend({
    bills: z.number(),
    color: z.string(),
  })
  .transform((input) => ({
    ...input,
    colorHex: input.color,
  }));

const billsByTagSchema = tagSchema
  .omit({ description: true })
  .extend({
    bills: z.number(),
    color: z.string(),
  })
  .transform((input) => ({
    ...input,
    colorHex: input.color,
  }));

const osadCurrentLocationSchema = z.object({
  type: z.string(),
  name: z.string(),
  trailer: z.number().nullish(),
  pullId: z.number().nullish(),
  trapId: z.number().nullish(),
  tripNumber: z.number().nullish(),
});

const osadSummarySchema = z.object({
  areaName: z.string(),
  bills: z.number(),
  pallets: z.number(),
  pieces: z.number(),
  weight: z.number(),
  handlingUnits: z.number(),
  billsByTags: z.array(billsByTagSchema),
});

const osadShipmentSchema = z
  .object({
    shipmentId: z.number(),
    proNumber: z.string(),
    reasonId: z.number(),
    reasonCode: z.string(),
    pallets: z.number(),
    pieces: z.number(),
    weight: z.number(),
    weightUnit: z.string(),
    handlingUnits: z.number(),
    tags: z.array(osadShipmentTagSchema),
    originSic: z.string(),
    destinationSic: z.string(),
    finalDestinationSic: z.string(),
    status: z.string(),
    dueServiceDate: z.date().nullable(),
    loadToSicId: z.number(),
    loadTo: z.string(),
    currentLocation: osadCurrentLocationSchema,
    appointmentStartTimestampUtc: z.date().optional(),
    appointmentEndTimestampUtc: z.date().optional(),
    shipper: shipperSchema.optional(),
    consignee: consigneeAndDeliverySchema.optional(),
    careOfDelivery: careOfDeliverySchema.nullable().optional(),
    deliveryAddress: consigneeAndDeliverySchema.optional(),
  })
  .transform((input) => ({
    ...input,
    serviceDueDate: input.dueServiceDate,
    destinationSicCode: input.destinationSic,
    originSicCode: input.originSic,
    appointmentDate: input.appointmentStartTimestampUtc,
    appointmentEndDate: input.appointmentEndTimestampUtc,
  }));

export const osadShipmentsSchema = z.object({
  summary: osadSummarySchema,
  shipments: z.array(osadShipmentSchema),
});

export type OSADShipment = z.infer<typeof osadShipmentSchema>;
export type OSADShipments = z.infer<typeof osadShipmentsSchema>;
